import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faMusic } from '@fortawesome/pro-light-svg-icons/faMusic';
import { faNewspaper } from '@fortawesome/pro-light-svg-icons/faNewspaper';
import { faMicrophone } from '@fortawesome/pro-light-svg-icons/faMicrophone';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { CampaignGoal } from '../hooks/useCampaignActions';
import { campaignMessages as messages } from './campaignMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the setup; each part uses its own slice of it */
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface GoalCopy {
  goal: CampaignGoal;
  icon: IconDefinition;
  title: MessageDescriptor;
  sub: MessageDescriptor;
  kicker: MessageDescriptor;
}

export const GOALS: GoalCopy[] = [
  {
    goal: 'playlists',
    icon: faMusic,
    title: messages.goalPlaylists,
    sub: messages.goalPlaylistsSub,
    kicker: messages.goalPlaylistsKicker,
  },
  {
    goal: 'press',
    icon: faNewspaper,
    title: messages.goalPress,
    sub: messages.goalPressSub,
    kicker: messages.goalPressKicker,
  },
  {
    goal: 'radio',
    icon: faMicrophone,
    title: messages.goalRadio,
    sub: messages.goalRadioSub,
    kicker: messages.goalRadioKicker,
  },
  {
    goal: 'mixed',
    icon: faSparkles,
    title: messages.goalMixed,
    sub: messages.goalMixedSub,
    kicker: messages.goalMixedKicker,
  },
];

export function isCampaignGoal(goal: string | undefined): goal is CampaignGoal {
  return GOALS.some((item) => item.goal === goal);
}

export function goalTitleOf(goal: string): MessageDescriptor {
  return GOALS.find((item) => item.goal === goal)?.title ?? messages.goalMixed;
}

/** The 4 goal cards of question 2 (design outreach-v2 b1). */
const GoalCardsComponent: React.FC<{ value: CampaignGoal; onChange: (goal: CampaignGoal) => void }> = ({
  value,
  onChange,
}) => {
  const intl = useIntl();
  const cards = useMemo(
    () =>
      GOALS.map((item) => {
        const on = item.goal === value;
        return (
          <button
            key={item.goal}
            type="button"
            className={clsx(styles.pickable, styles.goal, on && styles.picked)}
            onClick={() => onChange(item.goal)}
            aria-pressed={on}
            data-testid={`builder-goal-${item.goal}`}
          >
            <span className={styles.goalIcon}>
              <FontAwesomeIcon icon={item.icon} />
            </span>
            <b>{intl.formatMessage(item.title)}</b>
            <small>{intl.formatMessage(item.sub)}</small>
            <span className={styles.goalKicker}>{intl.formatMessage(item.kicker)}</span>
          </button>
        );
      }),
    [intl, onChange, value]
  );
  return <div className={styles.goals}>{cards}</div>;
};

export const GoalCards = TypedMemo(GoalCardsComponent);
